'use client'

import * as React from 'react'
import { ReactNode, useMemo } from 'react'
import { FieldValues, useController, UseControllerProps } from 'react-hook-form'
import { v4 } from 'uuid'

import GradientWrapper from '@/common/GradientWrapper'
import { cn } from '@/theme/utils'
import { UiLabel } from '@/ui/UiLabel'

type Props = React.ComponentProps<'input'>

const UiInput = React.forwardRef<HTMLInputElement, Props & { type?: string }>(
  ({ className, type, ...props }, ref) => {
    return (
      <input
        type={type}
        data-slot='input'
        spellCheck={false}
        autoCorrect='off'
        autoCapitalize='off'
        inputMode='text'
        ref={ref}
        className={cn(
          'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground border-input text-foreground flex h-12 w-full min-w-0 rounded-md border px-3 py-1 shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-sm placeholder:font-normal disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
          'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
          'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
          'bg-input autofill-text font-semibold',
          className,
        )}
        {...props}
      />
    )
  },
)

export type ControlledInputProps<T extends FieldValues> = Props & {
  label?: ReactNode
  leadingContent?: ReactNode
  trailingContent?: ReactNode
} & UseControllerProps<T>

function ControlledUiInput<T extends FieldValues>({
  name,
  control,
  rules,
  label,
  leadingContent,
  trailingContent,
  ...rest
}: ControlledInputProps<T>) {
  const id = useMemo(() => v4(), [])
  const { field, fieldState } = useController({ control, name, rules: rules })

  return (
    <div className='grid gap-2'>
      {label &&
        (() => {
          return (
            <UiLabel
              className='text-muted-foreground typography-m3-label-medium!'
              htmlFor={id}
            >
              {label}
            </UiLabel>
          )
        })()}
      <GradientWrapper className='relative isolate flex w-full'>
        {leadingContent}
        <UiInput
          {...rest}
          id={id}
          ref={field.ref}
          autoCapitalize='none'
          onChange={e => {
            rest.onChange?.(e)
            field.onChange(e)
          }}
          value={field.value}
        />
        {trailingContent}
      </GradientWrapper>
      {fieldState.error?.message && (
        <span className='text-destructive typography-m3-body-small'>
          {fieldState.error.message}
        </span>
      )}
    </div>
  )
}

export { ControlledUiInput, UiInput }
